import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { API_URL } from "../../utils/config";

const PAGE_SIZES = [10, 25, 50];

function idTipus(t) {
  return t ? Number(t.idTipusDeMaterials ?? t.idTipusDeMaterial) : null;
}

function esActiu(v) {
  return v === true || v === 1 || v === "1";
}

function Seleccionartipusdematerials({
  onTipusSelect,
  onConsultar,
  onModificar,
  onEsborrar,
  selectedTipus,
  filterText,
  setFilterText,
  categoriaFilter,
  setCategoriaFilter,
  mostrarActius,
  setMostrarActius,
  reloadToken,
  setTipusList,
}) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const [tipusMaterials, setTipusMaterials] = useState([]);
  const [filteredTipus, setFilteredTipus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "Categoria", direction: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const fetchTipusMaterials = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/Tipusdematerials.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada.IdEmpresa,
            mostrarActius: mostrarActius ? 1 : 0,
          }),
        });
        const result = await response.json();
        if (result.success) {
          setTipusMaterials(result.tipusDeMaterials || []);
        } else {
          setTipusMaterials([]);
        }
      } catch (error) {
        console.error("Error carregant Tipus de Materials:", error);
        setTipusMaterials([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTipusMaterials();
  }, [empresaSeleccionada, mostrarActius, reloadToken]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      try {
        const response = await fetch(`${API_URL}/Categories.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada.IdEmpresa }),
        });
        const result = await response.json();
        setCategories(result.success ? result.categories || [] : []);
      } catch (error) {
        console.error("Error carregant Categories:", error);
        setCategories([]);
      }
    };
    fetchCategories();
  }, [empresaSeleccionada]);

  useEffect(() => {
    const lowerFilter = (filterText || "").toLowerCase();
    const filtered = (tipusMaterials || []).filter((item) => {
      const matchesCategory =
        !categoriaFilter || Number(item.Categories_idCategories) === Number(categoriaFilter);
      const matchesFilter =
        (item.Tipus || "").toLowerCase().includes(lowerFilter) ||
        (item.Categoria || "").toLowerCase().includes(lowerFilter);
      return matchesCategory && matchesFilter;
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = (a[sortConfig.key] || "").toString().toLowerCase();
        const bValue = (b[sortConfig.key] || "").toString().toLowerCase();
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredTipus(filtered);
    setTipusList?.(filtered);
    setPage(1);
  }, [filterText, tipusMaterials, categoriaFilter, sortConfig, setTipusList]);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const obrirMenu = (e, tipus) => {
    e.stopPropagation();
    const id = idTipus(tipus);
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 200;
    const alt = 132;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(id);
  };

  const total = filteredTipus.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginaActual = Math.min(page, totalPages);
  const inici = (paginaActual - 1) * pageSize;
  const paginaItems = filteredTipus.slice(inici, inici + pageSize);
  const tipusMenu = menuObert ? filteredTipus.find((t) => idTipus(t) === menuObert) : null;

  const paginesVisibles = () => {
    const pages = [];
    const max = totalPages;
    const actual = paginaActual;
    const afegir = (n) => {
      if (!pages.includes(n)) pages.push(n);
    };
    afegir(1);
    for (let i = actual - 1; i <= actual + 1; i += 1) {
      if (i > 1 && i < max) afegir(i);
    }
    if (max > 1) afegir(max);
    return pages.sort((a, b) => a - b);
  };

  const renderAccions = (tipus) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === idTipus(tipus) ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, tipus)}
      >
        …
      </button>
    </div>
  );

  return (
    <div className="dv2-listBody">
      <div className="dv2-filterBar">
        <div className="dv2-filter dv2-filter--grow">
          <span className="dv2-filter-label">Cerca</span>
          <input
            type="text"
            className="dv2-input dv2-input-search"
            placeholder="Cerca per tipus o categoria…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
        <div className="dv2-filter">
          <span className="dv2-filter-label">Categoria</span>
          <select
            className="dv2-select"
            value={categoriaFilter}
            onChange={(e) => setCategoriaFilter(e.target.value)}
          >
            <option value="">Totes</option>
            {categories.map((cat) => (
              <option key={cat.idCategories} value={String(cat.idCategories)}>
                {cat.Categoria}
              </option>
            ))}
          </select>
        </div>
        <label className="dv2-filter dv2-filter--toggle">
          <span className="dv2-filter-label">Mostrar actius</span>
          <span className="dv2-toggleRow">
            <span className="dv2-toggle">
              <input
                type="checkbox"
                checked={mostrarActius}
                onChange={() => setMostrarActius(!mostrarActius)}
              />
              <span className="dv2-toggle-track" />
            </span>
            {mostrarActius ? "Actius" : "Inactius"}
          </span>
        </label>
      </div>

      {loading ? (
        <p className="dv2-empty">Carregant tipus de materials…</p>
      ) : filteredTipus.length === 0 ? (
        <p className="dv2-empty">No s'ha trobat cap tipus de material.</p>
      ) : (
        <>
          {isNarrow ? (
            <div className="dv2-recordList" style={{ display: "flex" }}>
              {paginaItems.map((tipus) => (
                <article
                  key={idTipus(tipus)}
                  className="dv2-record"
                  onClick={() => onTipusSelect?.(tipus)}
                  onDoubleClick={() => pucConsultar && onConsultar?.(tipus)}
                >
                  <div>
                    <p className="dv2-record-title">{tipus.Tipus || "—"}</p>
                    <p className="dv2-record-meta">
                      {tipus.Categoria || "Sense categoria"} · {esActiu(tipus.Actiu) ? "Actiu" : "Inactiu"}
                    </p>
                  </div>
                  {renderAccions(tipus)}
                </article>
              ))}
            </div>
          ) : (
            <div className="dv2-tableWrap">
              <table className="dv2-table">
                <thead>
                  <tr>
                    <th
                      className={sortConfig.key === "Categoria" ? "is-sorted" : ""}
                      onClick={() => handleSort("Categoria")}
                    >
                      Categoria {sortConfig.key === "Categoria" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                    </th>
                    <th
                      className={sortConfig.key === "Tipus" ? "is-sorted" : ""}
                      onClick={() => handleSort("Tipus")}
                    >
                      Tipus {sortConfig.key === "Tipus" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                    </th>
                    <th className="dv2-table-actions">Accions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginaItems.map((tipus) => (
                    <tr
                      key={idTipus(tipus)}
                      className={idTipus(selectedTipus) === idTipus(tipus) ? "is-selected" : ""}
                      onClick={() => onTipusSelect?.(tipus)}
                      onDoubleClick={() => pucConsultar && onConsultar?.(tipus)}
                    >
                      <td>{tipus.Categoria || "—"}</td>
                      <td>{tipus.Tipus || "—"}</td>
                      <td className="dv2-table-actions">{renderAccions(tipus)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="dv2-pagination">
            <span>
              Mostrant {inici + 1} a {Math.min(inici + pageSize, total)} de {total} resultats
            </span>
            <div className="dv2-pagination-pages">
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual <= 1}
                onClick={() => setPage(1)}
                aria-label="Primera pàgina"
              >
                «
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Pàgina anterior"
              >
                ‹
              </button>
              {paginesVisibles().map((n, idx, arr) => (
                <React.Fragment key={n}>
                  {idx > 0 && n - arr[idx - 1] > 1 && <span>…</span>}
                  <button
                    type="button"
                    className={`dv2-pageBtn ${n === paginaActual ? "is-active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                </React.Fragment>
              ))}
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Pàgina següent"
              >
                ›
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage(totalPages)}
                aria-label="Última pàgina"
              >
                »
              </button>
            </div>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Files per pàgina</span>
              <select
                className="dv2-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}
      {tipusMenu &&
        menuPos &&
        createPortal(
          <div
            className="dv2-menu dv2-menu--fixed"
            ref={menuRef}
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            {pucConsultar && (
              <button
                type="button"
                className="dv2-menu-item"
                onClick={() => {
                  setMenuObert(null);
                  onConsultar?.(tipusMenu);
                }}
              >
                Consultar
              </button>
            )}
            {pucModificar && (
              <button
                type="button"
                className="dv2-menu-item"
                onClick={() => {
                  setMenuObert(null);
                  onModificar?.(tipusMenu);
                }}
              >
                Modificar
              </button>
            )}
            {pucEsborrar && (
              <button
                type="button"
                className="dv2-menu-item is-danger"
                onClick={() => {
                  setMenuObert(null);
                  onEsborrar?.(tipusMenu);
                }}
              >
                Esborrar
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default Seleccionartipusdematerials;
